import React, { useState } from "react";
import {
  Modal,
  Card,
  Tag,
  Space,
  Typography,
  Button,
  Input,
  List,
  Avatar,
  Divider,
  Select,
  message,
  Spin,
  Badge,
  Row,
  Col
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { ITask, TaskStatus, TaskPriority } from "../../types/task.type";
import { taskApi } from "../../api/taskApi";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

interface TaskDetailsModalProps {
  task: ITask | null;
  visible: boolean;
  onClose: () => void;
  onUpdate: () => void;
  onTaskUpdate?: (updatedTask: ITask) => void;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  task,
  visible,
  onClose,
  onUpdate,
  onTaskUpdate
}) => {
  const { authState } = useAuth();
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const isAdmin = authState.user?.role === UserRole.Admin;
  const isAssigned = task?.assignedTo?._id === authState.user?._id;
  const canChangeStatus = isAdmin || isAssigned;

  const priorityColors = {
    [TaskPriority.Low]: "blue",
    [TaskPriority.Medium]: "orange",
    [TaskPriority.High]: "red",
    [TaskPriority.Urgent]: "purple"
  };

  const statusColors = {
    [TaskStatus.Pending]: "default",
    [TaskStatus.InProgress]: "processing",
    [TaskStatus.Completed]: "success",
    [TaskStatus.Cancelled]: "error"
  };

  const statusLabels = {
    [TaskStatus.Pending]: "Ожидает",
    [TaskStatus.InProgress]: "В работе",
    [TaskStatus.Completed]: "Завершена",
    [TaskStatus.Cancelled]: "Отменена"
  };

  const priorityLabels = {
    [TaskPriority.Low]: "Низкий",
    [TaskPriority.Medium]: "Средний",
    [TaskPriority.High]: "Высокий",
    [TaskPriority.Urgent]: "Срочный"
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !task) return;

    setLoading(true);
    try {
      await taskApi.addComment(task._id, commentText);
      message.success("Комментарий добавлен");
      setCommentText("");
      // Не обновляем весь список, просто добавляем комментарий локально
      if (task && onTaskUpdate) {
        const newComment = {
          text: commentText,
          createdBy: { _id: authState.user?._id || '', name: authState.user?.name || '', username: authState.user?.username || '' },
          createdAt: new Date().toISOString()
        };
        const updatedTask = { ...task, comments: [...task.comments, newComment] };
        onTaskUpdate(updatedTask);
      }
    } catch (error: any) {
      message.error(error.message || "Ошибка при добавлении комментария");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!task) return;

    setStatusLoading(true);
    try {
      await taskApi.updateTask(task._id, { status: newStatus });
      message.success("Статус задачи обновлен");
      onUpdate();
    } catch (error: any) {
      message.error(error.message || "Ошибка при обновлении статуса");
    } finally {
      setStatusLoading(false);
    }
  };

  const isOverdue = task?.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== TaskStatus.Completed;
  const isDueToday = task?.dueDate && dayjs(task.dueDate).isSame(dayjs(), 'day');

  return (
    <Modal
      title={
        <Space>
          <span>Детали задачи</span>
          {task && (
            <Badge status={statusColors[task.status] as any} text={statusLabels[task.status]} />
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {!task ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          {/* Заголовок и основная информация */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <Title level={3} style={{ margin: 0, flex: 1 }}>
                {task.title}
              </Title>
              <Space>
                <Tag color={priorityColors[task.priority]}>{priorityLabels[task.priority]}</Tag>
                {canChangeStatus && (
                  <Select
                    value={task.status}
                    onChange={handleStatusChange}
                    loading={statusLoading}
                    style={{ width: 120 }}
                  >
                    {Object.entries(statusLabels).map(([status, label]) => (
                      <Option key={status} value={status}>{label}</Option>
                    ))}
                  </Select>
                )}
              </Space>
            </div>

            {task.description && (
              <Paragraph style={{ marginBottom: 16 }}>
                {task.description}
              </Paragraph>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" size="small">
                  <Space>
                    <UserOutlined />
                    <Text strong>Назначен:</Text>
                    <Text>{task.assignedTo ? task.assignedTo.name : "Не назначен"}</Text>
                  </Space>
                  <Space>
                    <UserOutlined />
                    <Text strong>Создатель:</Text>
                    <Text>{task.createdBy.name}</Text>
                  </Space>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="small">
                  {task.dueDate && (
                    <Space>
                      <CalendarOutlined />
                      <Text strong>Срок:</Text>
                      <Text type={isOverdue ? "danger" : isDueToday ? "warning" : "secondary"}>
                        {dayjs(task.dueDate).format("DD.MM.YYYY HH:mm")}
                      </Text>
                    </Space>
                  )}
                  {task.completedAt && (
                    <Space>
                      <CheckCircleOutlined />
                      <Text strong>Завершена:</Text>
                      <Text>{dayjs(task.completedAt).format("DD.MM.YYYY HH:mm")}</Text>
                    </Space>
                  )}
                </Space>
              </Col>
            </Row>

            {task.tags.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Теги:</Text>
                <div style={{ marginTop: 8 }}>
                  {task.tags.map(tag => (
                    <Tag key={tag} style={{ marginBottom: 4 }}>{tag}</Tag>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Комментарии */}
          <Card title={
            <Space>
              <CommentOutlined />
              <span>Комментарии ({task.comments.length})</span>
            </Space>
          }>
            {/* Добавление комментария */}
            <div style={{ marginBottom: 16 }}>
              <TextArea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Добавить комментарий..."
                rows={3}
                style={{ marginBottom: 8 }}
              />
              <Button
                type="primary"
                onClick={handleAddComment}
                loading={loading}
                disabled={!commentText.trim()}
              >
                Добавить комментарий
              </Button>
            </div>

            <Divider />

            {/* Список комментариев */}
            <List
              dataSource={task.comments}
              renderItem={(comment, index) => (
                <List.Item key={index}>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <Space>
                        <Text strong>{comment.createdBy.name}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {dayjs(comment.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                      </Space>
                    }
                    description={
                      <Paragraph style={{ margin: 0, marginTop: 8 }}>
                        {comment.text}
                      </Paragraph>
                    }
                  />
                </List.Item>
              )}
              locale={{
                emptyText: "Нет комментариев"
              }}
            />
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default TaskDetailsModal; 